/**
 * Client-Side Image Compression & EXIF Stripping
 * Max dimensions: 1200x1200px
 * Max file size target: <= 200 KB
 */

export interface CompressionResult {
  file: File;
  sha256: string;
  originalSize: number;
  compressedSize: number;
  mimeType: string;
  previewUrl: string;
}

export async function compressAndHashImage(inputFile: File): Promise<CompressionResult> {
  // Validate input type
  if (!['image/jpeg', 'image/png'].includes(inputFile.type)) {
    throw new Error('Only JPEG and PNG files are accepted.');
  }

  const originalSize = inputFile.size;

  // Load image
  const img = await createImageBitmap(inputFile);
  const maxDim = 1200;
  let { width, height } = img;

  if (width > maxDim || height > maxDim) {
    if (width > height) {
      height = Math.round((height * maxDim) / width);
      width = maxDim;
    } else {
      width = Math.round((width * maxDim) / height);
      height = maxDim;
    }
  }

  // Draw to canvas (strips EXIF metadata automatically)
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context not available.');
  }

  // Fill with white background for transparency in PNGs converted to JPEG
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  // Iterative compression to target < 200KB
  let quality = 0.85;
  let blob: Blob | null = null;
  const targetBytes = 200 * 1024; // 200 KB

  do {
    blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
    });
    if (!blob) break;
    if (blob.size <= targetBytes || quality <= 0.4) {
      break;
    }
    quality -= 0.1;
  } while (quality > 0.3);

  if (!blob) {
    throw new Error('Failed to encode image to JPEG blob.');
  }

  // Compute SHA-256 hash using Web Crypto API
  const arrayBuffer = await blob.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const sha256 = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

  // Create preview object URL
  const previewUrl = URL.createObjectURL(blob);

  // Create clean File instance
  const compressedFile = new File([blob], `proof_${Date.now()}.jpg`, {
    type: 'image/jpeg',
  });

  return {
    file: compressedFile,
    sha256,
    originalSize,
    compressedSize: blob.size,
    mimeType: 'image/jpeg',
    previewUrl,
  };
}
